import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useShop } from '../context/ShopContext';
import { IoClose } from 'react-icons/io5';
import confetti from 'canvas-confetti';

const CheckoutModal = () => {
  const { isCheckoutOpen, setIsCheckoutOpen, cartTotal, cart, currency, placeOrder } = useShop();
  const [step, setStep] = useState('shipping');
  const [loading, setLoading] = useState(false);
  const [shiprocketRadar, setShiprocketRadar] = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);

  const [userDetails, setUserDetails] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const shippingCost = cartTotal > 1500 ? 0 : (currency.code === 'INR' ? 120 : 15);
  const total = cartTotal + shippingCost;

  const handlePincodeChange = (val) => {
    const pin = val.replace(/\D/g, '').slice(0, 6);
    let updated = { ...userDetails, pincode: pin };

    if (pin.length === 6) {
      if (pin.startsWith('629')) {
        updated.city = 'Nagercoil';
        updated.state = 'Tamil Nadu';
        setShiprocketRadar({
          serviceable: true,
          courier: 'BlueDart Same-Day Express Air',
          eta: 'Same-Day Dispatch (Nagercoil / Kanyakumari unit)'
        });
      } else if (pin.startsWith('6')) {
        updated.state = 'Tamil Nadu';
        setShiprocketRadar({
          serviceable: true,
          courier: 'Shiprocket Air Hub (BlueDart / Delhivery Express)',
          eta: '1-2 Business Days Priority Air'
        });
      } else {
        setShiprocketRadar({
          serviceable: true,
          courier: 'Shiprocket National Air Hub (Delhivery / BlueDart)',
          eta: '2-3 Business Days Priority Air'
        });
      }
    } else {
      setShiprocketRadar(null);
    }
    setUserDetails(updated);
  };

  const dispatchOrder = async (method, paymentId) => {
    // Note: for Razorpay this is now mostly handled via backend verification. 
    // This dispatchOrder is kept simple for COD fallback.
    setLoading(true);
    await placeOrder(userDetails);
    setLoading(false);
    
    // For COD
    if (method.includes('COD')) {
        setOrderSummary({ orderId: 'Pending Creation', awb: 'Pending Validation', method, paymentId });
        setStep('success');
        try { confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 }, colors: ['#ffffff', '#aaaaaa', '#444444'] }); } catch (e) {}
    }
  };

  const handleRazorpayPayment = async (e) => {
    e.preventDefault();
    if (!userDetails.name || !userDetails.phone || !userDetails.pincode || !userDetails.address) {
      alert('Please complete all required shipping fields.');
      return;
    }

    setLoading(true);

    try {
      // 1. Create order on the backend to get a Razorpay Order ID securely
      const response = await fetch('/api/orders/razorpay/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            cart: cart,
            region: currency.code === 'INR' ? 'IN' : 'US',
            userDetails: userDetails
        })
      });
      
      const orderData = await response.json();
      
      if (!response.ok) {
        alert(orderData.message || 'Error initiating payment');
        setLoading(false);
        return;
      }

      // 2. Setup Razorpay Options
      const options = {
        key: 'rzp_live_SmVeN1p4Ymvnh2',
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'MITOFORGE · Cordinal Mushrooms',
        description: 'Cordyceps Militaris Collection',
        image: '/logo-clean.png',
        order_id: orderData.id,
        handler: async function (res) {
          // 3. Verify Payment
          try {
            const verifyRes = await fetch('/api/orders/razorpay/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    razorpay_order_id: res.razorpay_order_id,
                    razorpay_payment_id: res.razorpay_payment_id,
                    razorpay_signature: res.razorpay_signature,
                    cart: cart,
                    region: currency.code === 'INR' ? 'IN' : 'US',
                    userDetails: userDetails
                })
            });
            
            const verifyData = await verifyRes.json();
            
            if (verifyRes.ok) {
                setLoading(false);
                setOrderSummary({ 
                    orderId: verifyData.internalOrderId, 
                    awb: verifyData.shiprocketAwb || 'Processing via Shiprocket', 
                    method: 'PREPAID (RAZORPAY)', 
                    paymentId: res.razorpay_payment_id 
                });
                setStep('success');
                try { confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 }, colors: ['#ffffff', '#aaaaaa', '#444444'] }); } catch (e) {}
            } else {
                alert('Payment Verification Failed: ' + verifyData.message);
            }
          } catch (err) {
             console.error(err);
             alert('Network error verifying payment.');
          }
        },
        prefill: {
          name: userDetails.name,
          email: userDetails.email,
          contact: userDetails.phone
        },
        notes: {
          address: userDetails.address + ', Pincode: ' + userDetails.pincode
        },
        theme: { color: '#000000' }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (res) {
        alert('Payment Authorization Failed. Reason: ' + res.error.description);
      });
      rzp.open();
      setLoading(false);

    } catch (err) {
      console.error(err);
      alert('Error initiating checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleCodPayment = (e) => {
    e.preventDefault();
    if (!userDetails.name || !userDetails.phone || !userDetails.pincode || !userDetails.address) {
      alert('Please complete all required shipping fields.');
      return;
    }
    dispatchOrder('CASH ON DELIVERY (SHIPROCKET COD)', 'COD');
  };

  if (!isCheckoutOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(20px)',
          zIndex: 3000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          overflowY: 'auto'
        }}
      >
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.94, opacity: 0 }}
          style={{
            background: '#0a0a0a',
            width: '100%',
            maxWidth: '560px',
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: '12px',
            border: '1px solid #333333',
            padding: '36px',
            position: 'relative',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.9)'
          }}
        >
          <button
            onClick={() => setIsCheckoutOpen(false)}
            style={{ position: 'absolute', top: '24px', right: '24px', color: '#888888' }}
          >
            <IoClose size={26} />
          </button>

          {step === 'shipping' && (
            <form onSubmit={(e) => { e.preventDefault(); setStep('payment'); }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
                <img src="/LOGO.jpeg" alt="MITOFORGE" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
                <div>
                  <h2 style={{ color: '#ffffff', fontSize: '1.35rem', fontWeight: 800 }}>Shipping Details</h2>
                  <div style={{ fontSize: '0.72rem', color: '#888888', fontWeight: 700, letterSpacing: '0.08em' }}>
                    DISPATCH VIA SHIPROCKET
                  </div>
                </div>
              </div>

              <div style={{
                background: '#101010', border: '1px solid #2a2a2a', borderRadius: '8px',
                padding: '12px 14px', marginBottom: '22px', color: '#aaaaaa', fontSize: '0.78rem', lineHeight: 1.5
              }}>
                <strong style={{ color: '#ffffff' }}>Pre-order:</strong> we're in pre-launch. Your order is reserved and
                dispatched once our FSSAI Central licence is issued — you won't be charged for shipping until then.
              </div>

              <div style={{ display: 'grid', gap: '14px', marginBottom: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <input
                    required
                    type="text"
                    placeholder="Full Name *"
                    style={inputStyle}
                    value={userDetails.name}
                    onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                  />
                  <input
                    required
                    type="tel"
                    placeholder="Mobile (+91) *"
                    style={inputStyle}
                    value={userDetails.phone}
                    onChange={(e) => setUserDetails({ ...userDetails, phone: e.target.value })}
                  />
                </div>

                <input
                  required
                  type="email"
                  placeholder="Email Address * (order updates & tracking)"
                  style={inputStyle}
                  value={userDetails.email}
                  onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                />

                <input
                  required
                  type="text"
                  placeholder="Complete Delivery Street Address *"
                  style={inputStyle}
                  value={userDetails.address}
                  onChange={(e) => setUserDetails({ ...userDetails, address: e.target.value })}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <input
                    required
                    type="text"
                    placeholder="PIN Code *"
                    maxLength={6}
                    style={{ ...inputStyle, borderColor: '#ffffff', fontWeight: 700 }}
                    value={userDetails.pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="City"
                    style={inputStyle}
                    value={userDetails.city}
                    onChange={(e) => setUserDetails({ ...userDetails, city: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="State"
                    style={inputStyle}
                    value={userDetails.state}
                    onChange={(e) => setUserDetails({ ...userDetails, state: e.target.value })}
                  />
                </div>
              </div>

              {shiprocketRadar && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  style={{
                    background: '#141414',
                    border: '1px solid #333333',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}
                >
                  <span style={{ fontSize: '1.4rem' }}>🛰️</span>
                  <div>
                    <div style={{ color: '#ffffff', fontWeight: 800, fontSize: '0.82rem', letterSpacing: '0.04em' }}>
                      SERVICEABLE ROUTE: {shiprocketRadar.courier.toUpperCase()}
                    </div>
                    <div style={{ color: '#888888', fontSize: '0.78rem', marginTop: '2px' }}>
                      {shiprocketRadar.eta}
                    </div>
                  </div>
                </motion.div>
              )}

              <div style={summaryStyle}>
                <div style={rowStyle}><span>Cart Subtotal</span><span>{currency.symbol}{cartTotal.toFixed(2)}</span></div>
                <div style={rowStyle}>
                  <span>Shiprocket Priority Air</span>
                  <span style={{ color: '#ffffff', fontWeight: 700 }}>
                    {shippingCost === 0 ? 'COMPLIMENTARY' : `${currency.symbol}${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                <div style={{ ...rowStyle, borderTop: '1px solid #222222', paddingTop: '14px', marginTop: '14px', fontSize: '1.25rem', fontWeight: 900, color: '#ffffff' }}>
                  <span>Total Due</span>
                  <span>{currency.symbol}{total.toFixed(2)}</span>
                </div>
              </div>

              <button type="submit" style={buttonStyle}>Proceed to Authorization →</button>
            </form>
          )}

          {step === 'payment' && (
            <div>
              <h2 style={{ color: '#ffffff', marginBottom: '8px', fontSize: '1.35rem', fontWeight: 800 }}>Authorization Method</h2>
              <p style={{ color: '#888888', marginBottom: '24px', fontSize: '0.88rem' }}>
                All transactions utilize 256-bit bank-grade SSL encryption.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                <button
                  type="button"
                  onClick={handleRazorpayPayment}
                  disabled={loading}
                  style={buttonStyle}
                >
                  Pay via Razorpay (UPI / Cards / NetBanking)
                </button>

                <button
                  type="button"
                  onClick={handleCodPayment}
                  disabled={loading}
                  style={{
                    ...buttonStyle,
                    background: 'transparent',
                    color: '#ffffff',
                    border: '1px solid #333333'
                  }}
                >
                  Cash on Delivery (Shiprocket COD)
                </button>
              </div>

              <button
                type="button"
                onClick={() => setStep('shipping')}
                style={{ width: '100%', padding: '10px', background: 'transparent', color: '#888888', fontSize: '0.82rem' }}
              >
                ← Return to Delivery Address
              </button>
            </div>
          )}

          {step === 'success' && orderSummary && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#ffffff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <span style={{ fontSize: '2rem', color: '#000000', fontWeight: 900 }}>✓</span>
              </div>

              <h2 style={{ color: '#ffffff', marginBottom: '8px', fontWeight: 900 }}>Order Received</h2>
              <p style={{ color: '#888888', fontSize: '0.9rem', marginBottom: '28px' }}>
                Your order summary and Shiprocket tracking reference have been generated.
              </p>

              <div style={{
                background: '#121212',
                border: '1px solid #282828',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'left',
                fontSize: '0.88rem',
                marginBottom: '28px',
                color: '#cccccc'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ color: '#888888' }}>Order Reference:</span>
                  <strong style={{ color: '#ffffff', fontFamily: 'monospace' }}>{orderSummary.orderId}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ color: '#888888' }}>Shiprocket AWB:</span>
                  <strong style={{ color: '#ffffff', fontFamily: 'monospace' }}>{orderSummary.awb}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888888' }}>Payment Mode:</span>
                  <span>{orderSummary.method}</span>
                </div>
              </div>

              <button
                onClick={() => { setIsCheckoutOpen(false); setStep('shipping'); }}
                style={buttonStyle}
              >
                Return to Viewport
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const inputStyle = {
  padding: '14px 16px',
  background: '#121212',
  border: '1px solid #282828',
  borderRadius: '6px',
  color: '#ffffff',
  width: '100%',
  outline: 'none',
  fontSize: '0.9rem',
  fontFamily: 'inherit'
};

const summaryStyle = {
  background: '#101010',
  border: '1px solid #222222',
  padding: '20px',
  borderRadius: '8px',
  marginTop: '20px',
  marginBottom: '24px'
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '10px',
  color: '#888888',
  fontSize: '0.9rem'
};

const buttonStyle = {
  width: '100%',
  padding: '16px',
  background: '#ffffff',
  color: '#000000',
  fontWeight: 800,
  borderRadius: '6px',
  textTransform: 'uppercase',
  fontSize: '0.92rem',
  letterSpacing: '0.08em',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

export default CheckoutModal;
